import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, set, get } from 'firebase/database';

const Run = () => {
  const { currentUser } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [currentSpeed, setCurrentSpeed] = useState<number | null>(null);
  const [currentPace, setCurrentPace] = useState<string>('--:--');
  const [readoutInterval, setReadoutInterval] = useState(30); // default 30 seconds
  const [speechRate, setSpeechRate] = useState(1.0);
  const [speechPitch, setSpeechPitch] = useState(1.0);
  const [speechVolume, setSpeechVolume] = useState(1.0);
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const watchPositionId = useRef<number | null>(null);
  const lastSpokenTime = useRef<number>(0);
  const wakeLockRef = useRef<any>(null);
  const speechSynthesis = window.speechSynthesis;
  
  // Load available voices
  useEffect(() => {
    // Define loadVoices function outside of the event handler
    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      if (voices.length > 0) {
        setAvailableVoices(voices);
        
        // Set default voice if none is selected yet
        if (selectedVoice === '') {
          // Try to find Google US English voice
          const googleUSVoice = voices.find(voice => 
            voice.name.includes('Google') && voice.lang === 'en-US'
          );
          
          if (googleUSVoice) {
            setSelectedVoice(googleUSVoice.voiceURI);
          } else {
            // Default to first English voice or just first voice
            const englishVoice = voices.find(voice => voice.lang.startsWith('en'));
            setSelectedVoice(englishVoice ? englishVoice.voiceURI : voices[0].voiceURI);
          }
        }
      }
    };
    
    // Initial load attempt
    loadVoices();
    
    // Set up event listener for when voices are loaded (especially in Chrome)
    speechSynthesis.onvoiceschanged = loadVoices;
    
    // Cleanup event listener on component unmount
    return () => {
      speechSynthesis.onvoiceschanged = null;
    };
  }, []); // Empty dependency array to run only once on mount
  
  // Load user settings
  useEffect(() => {
    if (!currentUser) return;
    
    const loadSettings = async () => {
      try {
        const runSettingsRef = ref(database, `run/${currentUser.uid}/settings`);
        const snapshot = await get(runSettingsRef);
        const settings = snapshot.val();
        
        if (settings) {
          if (settings.readoutInterval) setReadoutInterval(settings.readoutInterval);
          if (settings.speechRate) setSpeechRate(settings.speechRate);
          if (settings.speechPitch) setSpeechPitch(settings.speechPitch);
          if (settings.speechVolume) setSpeechVolume(settings.speechVolume);
          if (settings.selectedVoice) setSelectedVoice(settings.selectedVoice);
        }
      } catch (error) {
        console.error('Error loading run settings:', error);
      }
    };
    
    loadSettings();
  }, [currentUser]);
  
  // Save settings
  const saveSettings = async () => {
    if (!currentUser) return;
    
    try {
      const runSettingsRef = ref(database, `run/${currentUser.uid}/settings`);
      await set(runSettingsRef, {
        readoutInterval,
        speechRate,
        speechPitch,
        speechVolume,
        selectedVoice
      });
      
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to save settings');
      setTimeout(() => setError(''), 3000);
    }
  };
  
  // Handle speed updates
  const handlePositionUpdate = (position: GeolocationPosition) => {
    // Only use speed if it's available and valid
    if (position.coords.speed !== null && position.coords.speed !== undefined && position.coords.speed > 0) {
      const speedMs = position.coords.speed; // speed in m/s
      setCurrentSpeed(speedMs);
      
      // Convert to pace (min/km)
      if (speedMs > 0) {
        const paceInSecondsPerKm = 1000 / speedMs;
        const minutes = Math.floor(paceInSecondsPerKm / 60);
        const seconds = Math.floor(paceInSecondsPerKm % 60);
        const formattedPace = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        setCurrentPace(formattedPace);
        
        // Speak the pace if enough time has passed
        const now = Date.now();
        if (now - lastSpokenTime.current >= readoutInterval * 1000) {
          speakPace(formattedPace);
          lastSpokenTime.current = now;
        }
      } else {
        setCurrentPace('--:--');
      }
    } else {
      setCurrentSpeed(null);
      setCurrentPace('--:--');
    }
  };
  
  // Start tracking
  // Request wake lock to keep screen on
  const requestWakeLock = async () => {
    try {
      // Check if the Wake Lock API is supported
      if ('wakeLock' in navigator) {
        // @ts-ignore - TypeScript may not recognize the wakeLock API yet
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Wake Lock activated');
        
        // Add a listener to reacquire the wake lock if it's released
        wakeLockRef.current.addEventListener('release', () => {
          console.log('Wake Lock released');
          // Try to reacquire the wake lock if we're still tracking
          if (isRunning) {
            requestWakeLock();
          }
        });
      } else {
        console.log('Wake Lock API not supported');
      }
    } catch (err) {
      console.error('Error requesting wake lock:', err);
    }
  };
  
  // Release wake lock
  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release()
        .then(() => {
          wakeLockRef.current = null;
          console.log('Wake Lock released');
        })
        .catch((err: any) => {
          console.error('Error releasing wake lock:', err);
        });
    }
  };

  const startTracking = () => {
    setError('');
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }
    
    try {
      watchPositionId.current = navigator.geolocation.watchPosition(
        handlePositionUpdate,
        (err) => {
          setError(`Error: ${err.message}`);
          setIsRunning(false);
          releaseWakeLock();
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 5000
        }
      );
      
      setIsRunning(true);
      lastSpokenTime.current = Date.now();
      speakText('Tracking started');
      
      // Request wake lock to keep screen on
      requestWakeLock();
    } catch (error) {
      setError('Failed to start tracking');
      console.error(error);
    }
  };
  
  // Stop tracking
  const stopTracking = () => {
    if (watchPositionId.current !== null) {
      navigator.geolocation.clearWatch(watchPositionId.current);
      watchPositionId.current = null;
    }
    setIsRunning(false);
    speakText('Tracking stopped');
    
    // Release the wake lock when tracking stops
    releaseWakeLock();
  };
  
  // Speak pace
  const speakPace = (pace: string) => {
    const [minutes, seconds] = pace.split(':');
    speakText(`${minutes}, ${seconds}`);
  };
  
  // Generic speak function
  const speakText = (text: string) => {
    if (speechSynthesis) {
      // Cancel any ongoing speech
      speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = speechRate;
      utterance.pitch = speechPitch;
      utterance.volume = speechVolume;
      
      // Set selected voice if available
      if (selectedVoice) {
        // Force refresh the voices list to ensure we have the latest voices
        const currentVoices = speechSynthesis.getVoices();
        const voice = currentVoices.find(v => v.voiceURI === selectedVoice);
        
        if (voice) {
          console.log(`Using voice: ${voice.name} (${voice.lang})`);
          utterance.voice = voice;
        } else {
          console.log(`Selected voice not found: ${selectedVoice}`);
        }
      }
      
      speechSynthesis.speak(utterance);
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchPositionId.current !== null) {
        navigator.geolocation.clearWatch(watchPositionId.current);
      }
      speechSynthesis.cancel();
      releaseWakeLock();
    };
  }, []);
  
  return (
    <Container>
      <h1 className="my-4">Run Tracker</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Current Pace</Card.Title>
          <div className="alert alert-info" style={{ fontSize: '0.9rem' }}>
            Note: This app attempts to keep your screen on during tracking, but performance may vary by device. For best results, keep the app open while running.
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="display-1">{currentPace}</h2>
              <p className="text-muted">minutes per kilometer</p>
            </div>
            <div>
              {!isRunning ? (
                <Button 
                  variant="success" 
                  size="lg" 
                  onClick={startTracking}
                  style={{ fontSize: '1.5rem', padding: '0.75rem 1.5rem' }}
                >
                  Start
                </Button>
              ) : (
                <Button 
                  variant="danger" 
                  size="lg" 
                  onClick={stopTracking}
                  style={{ fontSize: '1.5rem', padding: '0.75rem 1.5rem' }}
                >
                  Stop
                </Button>
              )}
            </div>
          </div>
          {currentSpeed !== null && (
            <p className="mb-0">
              Speed: {(currentSpeed * 3.6).toFixed(1)} km/h
            </p>
          )}
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          <Card.Title>Settings</Card.Title>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Voice readout interval (seconds)</Form.Label>
              <Form.Control
                type="number"
                min="5"
                max="300"
                value={readoutInterval}
                onChange={(e) => setReadoutInterval(parseInt(e.target.value))}
                disabled={isRunning}
              />
              <Form.Text className="text-muted">
                How often the current pace will be read aloud (in seconds)
              </Form.Text>
            </Form.Group>

            <h5 className="mb-3 mt-4">Voice Settings</h5>
            
            <Form.Group className="mb-3">
              <Form.Label>Rate: {speechRate.toFixed(1)}</Form.Label>
              <Form.Range
                min="0.5"
                max="2"
                step="0.1"
                value={speechRate}
                onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
                disabled={isRunning}
              />
              <Form.Text className="text-muted">
                How fast the voice speaks (0.5 = slower, 2.0 = faster)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Pitch: {speechPitch.toFixed(1)}</Form.Label>
              <Form.Range
                min="0.5"
                max="2"
                step="0.1"
                value={speechPitch}
                onChange={(e) => setSpeechPitch(parseFloat(e.target.value))}
                disabled={isRunning}
              />
              <Form.Text className="text-muted">
                Voice pitch (0.5 = lower, 2.0 = higher)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Volume: {speechVolume.toFixed(1)}</Form.Label>
              <Form.Range
                min="0"
                max="1"
                step="0.1"
                value={speechVolume}
                onChange={(e) => setSpeechVolume(parseFloat(e.target.value))}
                disabled={isRunning}
              />
              <Form.Text className="text-muted">
                Voice volume (0 = silent, 1.0 = maximum)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Voice</Form.Label>
              <Form.Select
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                disabled={isRunning}
              >
                {availableVoices.map((voice) => (
                  <option key={voice.voiceURI} value={voice.voiceURI}>
                    {voice.name} ({voice.lang})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Select the voice to use for pace announcements
              </Form.Text>
              {selectedVoice && (
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => {
                    const voice = availableVoices.find(v => v.voiceURI === selectedVoice);
                    if (voice) {
                      const utterance = new SpeechSynthesisUtterance("This is a test of the selected voice");
                      utterance.voice = voice;
                      utterance.rate = speechRate;
                      utterance.pitch = speechPitch;
                      utterance.volume = speechVolume;
                      speechSynthesis.speak(utterance);
                    }
                  }}
                  disabled={isRunning}
                >
                  Test Voice
                </Button>
              )}
            </Form.Group>
            
            <Button 
              variant="primary" 
              onClick={saveSettings}
              disabled={isRunning}
            >
              Save Settings
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Run;
